import type { ComponentType } from 'react';
import type { TabParamList } from '../../navigation/types';
import type { MenuTabFlowKind } from './menuTabResolver';
import {
  MainTabCalendarScreen,
  MainTabDashboardScreen,
  ResolvedOrderScreen,
  MainTabAccountsScreen,
} from '../../navigation/mainTabs/screens';
import { resolveMenuTabComponent, type MenuTabBarIconFlow } from './menuTabResolver';

export { MAIN_TAB_INITIAL_ROUTE } from './tabConstants';

/** Stable slot ids — future org API can reference these. */
export type TabSlotDefinition = {
  slot_id: 'calendar' | 'menu' | 'home' | 'orders' | 'account';
  route_name: keyof TabParamList;
  label_key: string;
  /** When false, tab is omitted (e.g. online orders disabled). */
  default_visible: boolean;
  /** Future: org field name that toggles visibility. */
  api_visibility_key?: 'show_online_orders';
  resolve_component: (ctx: TabResolveContext) => ComponentType;
  deep_link?: string;
  android_lazy?: boolean;
};

export type TabResolveContext = {
  menu_flow_kind: MenuTabFlowKind | undefined;
};

export const MAIN_TAB_STYLES = {
  ios: { labelFontSize: 9 },
  android: {
    labelFontSize: 12,
    inactiveTintColor: '#5F6F66',
    navigatorLazy: true,
  },
} as const;

/** Default slot order (Calendar → Menu → Home → Orders → Account). */
export const TAB_SLOT_ORDER: TabSlotDefinition['slot_id'][] = ['calendar', 'menu', 'home', 'orders', 'account'];

export const TAB_SLOT_REGISTRY: TabSlotDefinition[] = [
  {
    slot_id: 'calendar',
    route_name: 'Calendar',
    label_key: 'tabs.calendar',
    default_visible: true,
    resolve_component: () => MainTabCalendarScreen,
    deep_link: 'calendar',
  },
  {
    slot_id: 'menu',
    route_name: 'DailyMenuFlow',
    label_key: 'tabs.menu',
    default_visible: true,
    resolve_component: ctx => resolveMenuTabComponent(ctx.menu_flow_kind),
    deep_link: 'menu',
  },
  {
    slot_id: 'home',
    route_name: 'Dashboard',
    label_key: 'tabs.dashboard',
    default_visible: true,
    resolve_component: () => MainTabDashboardScreen,
    deep_link: 'dashboard',
    android_lazy: false,
  },
  {
    slot_id: 'orders',
    route_name: 'Order',
    label_key: 'tabs.orders',
    default_visible: true,
    api_visibility_key: 'show_online_orders',
    resolve_component: () => ResolvedOrderScreen,
    deep_link: 'orders',
  },
  {
    slot_id: 'account',
    route_name: 'Account',
    label_key: 'tabs.account',
    default_visible: true,
    resolve_component: () => MainTabAccountsScreen,
    deep_link: 'account',
  },
];

export function resolveMenuTabLabelKey(flow_kind: MenuTabFlowKind | undefined): string {
  if (flow_kind === 'daily_menu' || flow_kind === 'food_daily_menu') {
    return 'tabs.dailyMenu';
  }
  return 'tabs.menu';
}

export type MainTabRoute = {
  name: keyof TabParamList;
  component: ComponentType;
  android_lazy?: boolean;
};

export type MainTabModel = {
  primary: string;
  screen_bg: string;
  tab_labels: Record<keyof TabParamList, string>;
  tab_routes: MainTabRoute[];
  menu_tab_flow: MenuTabBarIconFlow | undefined;
};

export function buildTabRoutesFromRegistry(
  ctx: TabResolveContext,
  show_online_orders: boolean,
): MainTabRoute[] {
  const slot_by_id = new Map(TAB_SLOT_REGISTRY.map(s => [s.slot_id, s]));

  return TAB_SLOT_ORDER.flatMap(slot_id => {
    const slot = slot_by_id.get(slot_id);
    if (!slot) {
      return [];
    }
    if (slot.api_visibility_key === 'show_online_orders' && !show_online_orders) {
      return [];
    }
    return [
      {
        name: slot.route_name,
        component: slot.resolve_component(ctx),
        android_lazy: slot.android_lazy,
      },
    ];
  });
}

export function buildTabLabelsFromRegistry(
  t: (key: string) => string,
  menu_flow_kind: MenuTabFlowKind | undefined,
): Record<keyof TabParamList, string> {
  const ctx: TabResolveContext = { menu_flow_kind };
  const labels = {} as Record<keyof TabParamList, string>;

  for (const slot of TAB_SLOT_REGISTRY) {
    const key = slot.slot_id === 'menu' ? resolveMenuTabLabelKey(menu_flow_kind) : slot.label_key;
    labels[slot.route_name] = t(key);
  }

  return labels;
}

export function getTabDeepLinkPaths(): Record<string, keyof TabParamList> {
  const out: Record<string, keyof TabParamList> = {};
  for (const slot of TAB_SLOT_REGISTRY) {
    if (slot.deep_link) {
      out[slot.deep_link] = slot.route_name;
    }
  }
  return out;
}

